import ReportMetric from './ReportMetric'
import ReportChartWithTitle from './ReportChartWithTitle'
import {
  formatBarChartData,
  getReportMetricProps,
  getCombinedMaxValue,
} from '../helpers'
import { CircularProgress } from '@mui/material'
import { Stack } from 'ui/baseComponents/Stack'
import BarChart from 'ui/baseComponents/Charts/BarChart'
import { PERIODS } from 'ui/clinicianScreens/Patient/Report/consts'
import { useReportTranslation } from 'utilities/i18n'

const ReportBarChart = ({
  title,
  // Localized display text; falls back to the English identity so existing
  // callers keep working. `title` itself stays English for chartId builders.
  displayTitle,
  currentData,
  prevData,
  diff,
  recentPeriodColor,
  previousPeriodColor,
}) => {
  const { t } = useReportTranslation()
  // PERIODS stays the stable series identity keying the chart data, so the
  // localized legend text can change without reshaping the data or the DOM
  // hooks that Ghost Inspector relies on.
  const yAxisUnit = t('report.chart.yAxisMinutes')

  const recentPeriodLegendItem = {
    dataKey: PERIODS.recent,
    name: t('report.chart.periodRecent'),
    color: recentPeriodColor,
  }
  const previousPeriodLegendItem = {
    dataKey: PERIODS.previous,
    name: t('report.chart.periodPrevious'),
    color: previousPeriodColor,
  }

  const noData = !currentData?.numberOfDaysWithData
  const noPrevData = !prevData?.numberOfDaysWithData
  const formattedCurrentData = formatBarChartData(currentData, PERIODS.recent)
  const formattedPrevData = formatBarChartData(prevData, PERIODS.previous)
  const reportMetricProps = getReportMetricProps(currentData, prevData, diff)
  const yAxisMax = getCombinedMaxValue(currentData, prevData)

  // BarChart renders `Time with ${title}` as the legend prefix, so it needs the
  // localized text. chartId is built from the English `title` separately.
  const chartTitle = displayTitle ?? title

  const titleProps = {
    title: chartTitle,
    noData,
    subtitle: t('report.common.avgDay'),
    trend: <ReportMetric {...reportMetricProps} />,
  }

  return (
    <ReportChartWithTitle {...titleProps}>
      {!noData ? (
        <>
          <BarChart
            {...{
              title: chartTitle,
              data: formattedCurrentData,
              legendItem: recentPeriodLegendItem,
              yAxisMax,
              yAxisUnit,
              legend: true,
              isAnimationActive: false,
              height: 150,
              chartId: `${title.toLowerCase()}-chart-recent`,
            }}
          />

          {!noPrevData && (
            <BarChart
              {...{
                title: chartTitle,
                data: formattedPrevData,
                legendItem: previousPeriodLegendItem,
                yAxisMax,
                yAxisUnit,
                legend: true,
                isAnimationActive: false,
                height: 150,
                chartId: `${title.toLowerCase()}-chart-previous`,
              }}
            />
          )}
        </>
      ) : (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      )}
    </ReportChartWithTitle>
  )
}

export default ReportBarChart
